import { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

// Renders the monthly 6X6 / 3X3 digest as a stack of category-coded cards.
// Section labels come straight from the model's own `**Section.**` markers.

const SECTION_FAMILIES = {
  overall: {
    display: "OVERALL",
    bar: "#17a065",
    text: "#0f8f57",
    aliases: ["overall", "momentum"],
  },
  trends: {
    display: "TRENDS",
    bar: "#4f46e5",
    text: "#3730a3",
    aliases: ["trends", "trend", "metrics"],
  },
  products: {
    display: "PRODUCTS",
    bar: "#7c3aed",
    text: "#6d28d9",
    aliases: ["products", "product", "brand", "brands"],
  },
  coverage: {
    display: "COVERAGE",
    bar: "#0d76b0",
    text: "#0a5f8f",
    aliases: ["coverage", "cohort", "targeting"],
  },
  region: {
    display: "REGION",
    bar: "#0d76b0",
    text: "#0a5f8f",
    aliases: ["region", "territory"],
  },
  whitespace: {
    display: "WHITESPACE",
    bar: "#0d9488",
    text: "#0a7a70",
    aliases: ["whitespace", "opportunity"],
  },
  field: {
    display: "FIELD",
    bar: "#f0872b",
    text: "#c26305",
    aliases: [
      "field",
      "field effort",
      "effort",
      "efficiency",
      "action",
      "actions",
    ],
  },
  funnel: {
    display: "FUNNEL",
    bar: "#e63946",
    text: "#c92c38",
    aliases: ["funnel", "funnel health", "funnel leak"],
  },
  // v2.4: Competitors beat added to the Stage-3 digest candidate set.
  // Amber — thematically warm-cautionary and landscape-oriented,
  // distinct from Field's action-oriented orange.
  competitors: {
    display: "COMPETITORS",
    bar: "#d97706",
    text: "#b45309",
    aliases: ["competitors", "competitor", "landscape"],
  },
  caveat: {
    display: "CAVEAT",
    bar: "#e63946",
    text: "#c92c38",
    aliases: ["caveat", "warning", "caveats"],
  },
};

// Flattened alias -> style lookup, built once at module load.
const SECTION_STYLES = Object.fromEntries(
  Object.values(SECTION_FAMILIES).flatMap((family) =>
    family.aliases.map((alias) => [
      alias,
      { label: family.display, bar: family.bar, text: family.text },
    ]),
  ),
);

// Caveat beats are hidden from the card grid but stay in the raw payload.
const HIDDEN_SECTION_ALIASES = new Set(SECTION_FAMILIES.caveat.aliases);

const FALLBACK_STYLE = { label: null, bar: "#64748b", text: "#475569" };

// Section markers are always `**Titlecase.**` — excludes figure/entity emphasis.
const SECTION_MARKER_RE = /\*\*([A-Z][A-Za-z ]{1,30}\.)\*\*/g;

const COLORS = {
  sparkle: "#f0872b",
  titleText: "#111827",
  bodyText: "#111827",
  aiPillBg: "#fef1e0",
  aiPillText: "#c2620e",
  territoryPillBg: "#f3f0ea",
  territoryPillText: "#6b4c1f",
  datePillBg: "#eef4f8",
  datePillText: "#0b3d5c",
  cardBg: "#f7f8fa",
  scrollThumb: "#d1d5db",
};

const CARD_SHELL_SX = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: "8px",
  border: "0.5px solid #F0F0F0",
  boxShadow: "1px 4px 4px 0px #00000008",
};

const CARD_CONTENT_SX = {
  p: 2.5,
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const HEADER_ROW_SX = { flexShrink: 0, mb: 1.75, gap: 1 };

const SECTION_LIST_SX = {
  flex: "1 1 0",
  minHeight: 0,
  overflowY: "auto",
  pr: 0.5,
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: COLORS.scrollThumb,
    borderRadius: "4px",
  },
};

// Non-"ok" statuses all render the same heading + caption shape.
const STATUS_MESSAGE_CONFIG = {
  pending_phase_2: {
    heading: "Coming soon",
    fallbackMessage:
      "Per-scope report summaries are being enabled for this role.",
  },
  no_data: {
    heading: "No report this cycle",
    fallbackMessage:
      "The National Performance Excel report has not been ingested this cycle.",
  },
  no_scope: {
    heading: "No data scope mapped",
    fallbackMessage: "No region or territory is mapped to this account.",
  },
  no_material_findings: {
    heading: "Nothing material this cycle",
    fallbackMessage:
      "This scope did not carry enough activity for a material summary.",
  },
};

// ---------------------------------------------------------------------------
// Digest parsing
// ---------------------------------------------------------------------------

function isHiddenSection(label) {
  const key = (label || "").trim().replace(/\.+$/, "").toLowerCase();
  return HIDDEN_SECTION_ALIASES.has(key);
}

function styleFor(label) {
  const key = (label || "").trim().replace(/\.+$/, "").toLowerCase();
  if (!key) return FALLBACK_STYLE;

  const hit = SECTION_STYLES[key];
  if (hit) return hit;

  return { ...FALLBACK_STYLE, label: key.toUpperCase() };
}

// Splits a digest paragraph on inline `**Section.**` markers into cards.
// No markers found -> single unlabelled card with the full paragraph.
function parseDigestSections(paragraph) {
  if (!paragraph || typeof paragraph !== "string") return [];

  SECTION_MARKER_RE.lastIndex = 0;
  const markers = [];
  let match;
  while ((match = SECTION_MARKER_RE.exec(paragraph)) !== null) {
    markers.push({
      label: match[1],
      start: match.index,
      end: SECTION_MARKER_RE.lastIndex,
    });
  }

  if (markers.length === 0) {
    return [{ label: null, body: paragraph.trim() }];
  }

  const sections = [];
  for (let i = 0; i < markers.length; i++) {
    const marker = markers[i];
    const nextStart =
      i + 1 < markers.length ? markers[i + 1].start : paragraph.length;
    const body = paragraph.slice(marker.end, nextStart).trim();

    if (!body) continue;
    if (isHiddenSection(marker.label)) continue;

    sections.push({ label: marker.label, body });
  }
  return sections;
}

// Strips stray inline `**bold**` emphasis left over inside a section body.
function stripInlineBold(text) {
  if (!text) return "";
  return String(text).replace(/\*\*(.+?)\*\*/g, "$1");
}

function formatIngestedAt(raw) {
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------

function SectionCard({ label, body }) {
  const style = useMemo(() => styleFor(label), [label]);

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "6px",
        bgcolor: COLORS.cardBg,
        pl: 2,
        pr: 1.75,
        py: 1.25,
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 6,
          bottom: 6,
          width: "4px",
          borderRadius: "4px",
          bgcolor: style.bar,
        },
      }}
    >
      {style.label && (
        <Typography
          component="span"
          sx={{
            display: "block",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: style.text,
            mb: 0.25,
          }}
        >
          {style.label}
        </Typography>
      )}
      <Typography
        sx={{ fontSize: "13.5px", color: COLORS.bodyText, lineHeight: 1.5 }}
      >
        {stripInlineBold(body)}
      </Typography>
    </Box>
  );
}

function SectionCardSkeleton({ barColor = "#cbd5e1", lines = 2 }) {
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "6px",
        bgcolor: COLORS.cardBg,
        pl: 2,
        pr: 1.75,
        py: 1.25,
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 6,
          bottom: 6,
          width: "4px",
          borderRadius: "4px",
          bgcolor: barColor,
        },
      }}
    >
      <Skeleton
        variant="text"
        width={70}
        height={16}
        animation="wave"
        sx={{ mb: 0.5 }}
      />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? "55%" : "95%"}
          height={18}
          animation="wave"
        />
      ))}
    </Box>
  );
}

const SKELETON_BAR_COLORS = [
  SECTION_FAMILIES.overall.bar,
  SECTION_FAMILIES.trends.bar,
  SECTION_FAMILIES.field.bar,
  SECTION_FAMILIES.region.bar,
];

function SixBySummarySkeleton({ title }) {
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={HEADER_ROW_SX}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <AutoAwesomeIcon
            sx={{ color: COLORS.sparkle, fontSize: "20px", opacity: 0.5 }}
          />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: "16px", color: COLORS.titleText }}
          >
            {title}
          </Typography>
          <Skeleton
            variant="rounded"
            width={92}
            height={20}
            animation="wave"
            sx={{ borderRadius: "10px" }}
          />
        </Stack>
        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0, mr: 3 }}>
          <Skeleton
            variant="rounded"
            width={80}
            height={22}
            animation="wave"
            sx={{ borderRadius: "6px" }}
          />
          <Skeleton
            variant="rounded"
            width={110}
            height={22}
            animation="wave"
            sx={{ borderRadius: "6px" }}
          />
        </Stack>
      </Stack>

      <Stack
        spacing={1.25}
        sx={{ flex: "1 1 0", minHeight: 0, overflow: "hidden", pr: 0.5 }}
      >
        {SKELETON_BAR_COLORS.map((color, i) => (
          <SectionCardSkeleton
            key={color}
            barColor={color}
            lines={i === SKELETON_BAR_COLORS.length - 1 ? 1 : 2}
          />
        ))}
      </Stack>
    </>
  );
}

function CenteredMessage({ children }) {
  return (
    <Stack
      sx={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        color: "text.secondary",
        textAlign: "center",
        p: 3,
      }}
    >
      {children}
    </Stack>
  );
}

function StatusPlaceholder({ heading, message, maxWidth = 340 }) {
  return (
    <CenteredMessage>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {heading}
      </Typography>
      <Typography variant="caption" sx={{ maxWidth }}>
        {message}
      </Typography>
    </CenteredMessage>
  );
}

function SummaryHeader({ title, territoryCount, ingestedAt, showMetaChips }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={HEADER_ROW_SX}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ minWidth: 0 }}
      >
        <AutoAwesomeIcon sx={{ color: COLORS.sparkle, fontSize: "20px" }} />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: "16px",
            lineHeight: 1.2,
            color: COLORS.titleText,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={title}
        >
          {title}
        </Typography>
        <Chip
          size="small"
          label="AI GENERATED"
          sx={{
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            height: "20px",
            bgcolor: COLORS.aiPillBg,
            color: COLORS.aiPillText,
            borderRadius: "10px",
            flexShrink: 0,
            "& .MuiChip-label": { px: 1 },
          }}
        />
      </Stack>

      {showMetaChips && (
        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0, mr: 3 }}>
          {Number.isFinite(territoryCount) && territoryCount > 0 && (
            <Chip
              size="small"
              label={`${territoryCount} territories`}
              sx={{
                fontSize: "10px",
                height: "22px",
                bgcolor: COLORS.territoryPillBg,
                color: COLORS.territoryPillText,
              }}
            />
          )}
          {ingestedAt && (
            <Chip
              size="small"
              label={formatIngestedAt(ingestedAt)}
              sx={{
                fontSize: "10px",
                height: "22px",
                bgcolor: COLORS.datePillBg,
                color: COLORS.datePillText,
              }}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Widget
// ---------------------------------------------------------------------------

export default function SixBySummaryCard({
  title = "3X3 6X6 Summary",
  data = null,
  isLoading = false,
  isError = false,
}) {
  const status = data?.status ?? null;
  const reportSummary = data?.report_summary ?? "";
  const ingestedAt = data?.ingested_at ?? null;
  const message = data?.message ?? "";
  const territoryCount = data?.territory_count;

  const sections = useMemo(
    () => parseDigestSections(reportSummary),
    [reportSummary],
  );

  const renderBody = () => {
    if (isError) {
      return (
        <CenteredMessage>
          <Typography variant="body2" color="error">
            Could not load the report summary.
          </Typography>
        </CenteredMessage>
      );
    }

    const statusConfig = STATUS_MESSAGE_CONFIG[status];
    if (statusConfig) {
      return (
        <StatusPlaceholder
          heading={statusConfig.heading}
          message={message || statusConfig.fallbackMessage}
          maxWidth={
            status === "no_scope" || status === "no_material_findings"
              ? 360
              : 340
          }
        />
      );
    }

    if (status === "ok" && sections.length > 0) {
      return (
        <Stack spacing={1.25} sx={SECTION_LIST_SX}>
          {sections.map((section, i) => (
            <SectionCard
              key={`${section.label || "sec"}-${i}`}
              label={section.label}
              body={section.body}
            />
          ))}
        </Stack>
      );
    }

    return (
      <CenteredMessage>
        <Typography variant="body2" color="text.secondary">
          No summary available.
        </Typography>
      </CenteredMessage>
    );
  };

  return (
    <Card variant="outlined" sx={CARD_SHELL_SX}>
      <CardContent sx={CARD_CONTENT_SX}>
        {isLoading ? (
          <SixBySummarySkeleton title={title} />
        ) : (
          <>
            <SummaryHeader
              title={title}
              territoryCount={territoryCount}
              ingestedAt={ingestedAt}
              showMetaChips={false}
            />
            {renderBody()}
          </>
        )}
      </CardContent>
    </Card>
  );
}
